import React, { useMemo } from 'react'
import { Box } from '@mui/material'

import {
  BASELINE_HEIGHT,
  CHART_INNER_HEIGHT,
  CHART_PADDING_X,
  CURRENT_BAR_GRADIENT,
  MOVEMENT_BAR_GAP,
  MOVEMENT_BAR_WIDTH,
  PREVIOUS_BAR_BACKGROUND,
  getBarHeight
} from './comparisonBarChartConstants'

interface AnimatedBarProps {
  value: number
  maxValue: number
  animate: boolean
  width: number
  ariaLabel: string
}

export const AnimatedPreviousBar: React.FC<AnimatedBarProps> = ({ value, maxValue, animate, width, ariaLabel }) => {
  const barHeight = useMemo(() => getBarHeight(value, maxValue), [value, maxValue])

  if (barHeight === 0) {
    return null
  }

  return (
    <Box
      role="img"
      aria-label={ariaLabel}
      sx={{
        position: 'absolute',
        bottom: `${BASELINE_HEIGHT}px`,
        left: `${CHART_PADDING_X}px`,
        width: `${width}px`,
        height: `${CHART_INNER_HEIGHT}px`,
        display: 'flex',
        alignItems: 'flex-end',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: `${barHeight}px`,
          background: PREVIOUS_BAR_BACKGROUND,
          borderRadius: '8px 8px 0 0',
          transformOrigin: 'bottom',
          transform: animate ? 'scaleY(1)' : 'scaleY(0.25)',
          opacity: animate ? 1 : 0,
          transition: 'transform 700ms ease-out, opacity 700ms ease-out',
          border: '1px solid rgba(255, 255, 255, 0.12)'
        }}
      />
    </Box>
  )
}

export const AnimatedCurrentBar: React.FC<AnimatedBarProps> = ({ value, maxValue, animate, width, ariaLabel }) => {
  const barHeight = useMemo(() => getBarHeight(value, maxValue), [value, maxValue])

  if (barHeight === 0) {
    return null
  }

  return (
    <Box
      role="img"
      aria-label={ariaLabel}
      sx={{
        position: 'absolute',
        bottom: `${BASELINE_HEIGHT}px`,
        right: `${CHART_PADDING_X}px`,
        width: `${width}px`,
        height: `${CHART_INNER_HEIGHT}px`,
        display: 'flex',
        alignItems: 'flex-end',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: `${barHeight}px`,
          transform: animate ? 'translateY(0)' : `translateY(${barHeight}px)`,
          transition: 'transform 800ms ease-out',
          borderRadius: '8px 8px 0 0',
          background: CURRENT_BAR_GRADIENT,
          border: '1px solid rgba(255, 255, 255, 0.25)',
          boxShadow: '0px 6px 16px rgba(255, 171, 64, 0.3), 0px 2px 8px rgba(0, 0, 0, 0.45)'
        }}
      />
    </Box>
  )
}

interface MovementBarsProps {
  heights: number[]
  animate: boolean
  left: number
}

export const MovementBars: React.FC<MovementBarsProps> = ({ heights, animate, left }) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: `${BASELINE_HEIGHT}px`,
        left: `${left}px`,
        display: 'flex',
        gap: `${MOVEMENT_BAR_GAP}px`,
        height: `${CHART_INNER_HEIGHT}px`,
        alignItems: 'flex-end'
      }}
    >
      {heights.map((height, index) => (
        <Box
          key={`movement-${index}`}
          sx={{
            width: `${MOVEMENT_BAR_WIDTH}px`,
            height: `${height}px`,
            background: 'rgba(120, 144, 156, 0.18)',
            borderRadius: '4px 4px 0 0',
            boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.35)',
            transformOrigin: 'bottom',
            transform: animate ? 'scaleY(1)' : 'scaleY(0)',
            transition: `transform 650ms ease-out ${index * 70}ms`
          }}
        />
      ))}
    </Box>
  )
}
